import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { Loader } from 'components/atoms/Loader';
import { getRegisteredFirearm } from 'requests/firearm';
import { RegisteredFirearmDetails } from 'components/organisms/RegisteredFirearmDetails';
import {
  IRegisteredFirearmAttachmentProps,
  IRegisteredFirearmData,
} from 'models/registeredFirearm';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Accordion } from 'components/molecules/Accordion';
import { EPerPages, ISortOptions } from 'models/table';
import { ISearchTransactionFormValues } from 'models/form';
import { TransactionSearchForm } from 'pages/Firearms/TransactionSearchForm';
import { Table } from 'components/molecules/Table';
import {
  firearmAttachmentsTableColumns,
  firearmLicencesTableColumns,
  firearmTransactionsTableColumns,
  mapFirearmAttachmentToDataSource,
  mapFirearmLicenceToDataSource,
  mapFirearmTransactionToDataSource,
} from 'pages/Firearms/helpers';
import { useFirearms } from 'pages/Firearms/hooks';
import { Pagination } from 'components/molecules/Pagination';
import { CardList } from 'components/molecules/CardList';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { FirearmTransactionTableCard } from 'pages/Firearms/FirearmTransactionCard';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { Box, Tooltip, Typography } from '@mui/material';
import { CommentAccordion } from 'components/molecules/CommentAccordion';
import { colors } from 'theme/colors';
import { useForm } from 'react-hook-form';
import { ActionTypes } from 'state/actions/alert';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { FirearmAttachmentTableCard } from 'pages/Firearms/FirearmAttachmentTableCard';
import { useTranslations } from 'hooks/useTranslations';
import { StyledButton } from 'pages/Firearms/ViewRegisteredFirearmPage/styles';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { EFirearmStateId } from 'models/firearm';
import { useAccount } from 'pages/AccountPage/hooks';
import { useAuth } from 'models/authContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { exportFirearmTransactionsCSV } from 'requests/report';
import { isExportNotAvailable } from 'constants/Export';
import { clearAlertTimeout, EAlertTime, formatDateTimeToIso } from 'utils/date';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { usePrivilegesValidation } from 'utils/privilegesValidations';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import {
  ELegalEntityTypes,
  ELegalEntityTypesLabel,
  LETypes,
} from 'constants/LegalEntityTypes';
import { useQuery } from 'hooks/useQuery';
import { useDictionary } from 'models/dictionaryContext';
import { FirearmLicenceTableCard } from '../FirearmLicenceTableCard';

const ViewRegisteredFirearmPage = () => {
  const { pathname }: any = useLocation();
  const { state: routerState }: any = useLocation();
  const query = useQuery();
  const {
    id = '',
    licenceId = '',
    legalEntityId = '',
  } = useParams<{ id: string; licenceId: string; legalEntityId: string }>();
  const type: string | null = query.get('type')?.trim() || null;
  const expand: string | null = query.get('expand');
  const { user, getUser } = useAuth();
  const navigate = useNavigate();
  const { selectedLanguage } = useLang();
  const { getAssignedSelfLegalsToUserData } = useAccount();
  const [isFirearmLoading, setIsFirearmLoading] = useState<boolean>(false);
  const [isFirearmCommentsLoading, setIsFirearmCommentsLoading] = useState<boolean>(false);
  const [firearmDetailsAllExpanded, setFirearmDetailsAllExpanded] = useState<boolean>(false);
  const [firearm, setFirearm] = useState<IRegisteredFirearmData | null>();
  const { isMobile, isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();

  const isFromLicence =
    pathname.indexOf(
      ERouteLinks.ViewRegisteredFirearmFromLicence.replace(':id', id)
        .replace(':legalEntityId', legalEntityId as string)
        .replace(':licenceId', licenceId as string),
    ) !== -1;

  const [currentPageLicences, setCurrentPageLicences] = useState<number>(1);
  const [currentPageFT, setCurrentPageFT] = useState<number>(1);
  const [perPageFT, setPerPageFT] = useState<EPerPages>(EPerPages.perPage25);
  const [perPageLicences, setPerPageLicences] = useState<EPerPages>(EPerPages.perPage25);
  const [transactionQuery, setTransactionQuery] = useState<
    Partial<ISearchTransactionFormValues>
  >({});
  const [transactionSort, setTransactionSort] = useState<ISortOptions | null>(null);
  const [licenceSort, setLicenceSort] = useState<ISortOptions | null>(null);
  const [currentPageFComments, setCurrentPageFComments] = useState<number>(1);
  const [perPageFComments, setPerPageFComments] = useState<EPerPages>(EPerPages.perPage25);
  const [currentPageFA, setCurrentPageFA] = useState<number>(1);
  const [perPageFA, setPerPageFA] = useState<EPerPages>(EPerPages.perPage25);
  const [attachmentSort, setAttachmentSort] = useState<ISortOptions | null>(null);
  const {
    setAlert,
    samePageAlert,
    transactionAlert,
    nextPageAlert,
    firearmAlert,
    clearAlert,
  } = useAlert();
  const { midDateFormat, longDateFormat, shortDateFormat, getProperties } =
    useGlobalProperty();
  const [isExportLoading, setIsExportloading] = useState(false);
  const [isTransactionPaginationLoading, setIsTransactionPaginationLoading] = useState(false);
  const [isAttachmentPaginationLoading, setIsAttachmentPaginationLoading] = useState(false);
  const [isCommentPaginationLoading, setIsCommentPaginationLoading] = useState(false);
  const [isLicencesPaginationLoading, setIsLicencesPaginationLoading] = useState(false);
  const {
    discardableFirearmTransactionId,
    firearmTransactions,
    getFirearmTransactionsData,
    firearmComments,
    getFirearmCommentsData,
    paginatorFComments,
    paginatorFT,
    paginatorLicences,
    getFirearmLicencesData,
    firearmLicences,
    getDiscardableFirearmTransactionData,
    lastTransaction,
    firearmAttachments,
    getFirearmAttachmentsData,
    paginatorFA,
    loadDictionaries,
    transactionType,
  } = useFirearms();
  const { account, getSelfAccountData } = useAccount();

  const { weaponLicenceStatus, getWeaponLicenceStatus } = useDictionary();

  useEffect(() => {
    (async function getAccountDataInit() {
      if (user?.userId) {
        await getSelfAccountData();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const { hasPermissions } = usePermission();
  const { isAnyLicenceAuthorityValid } = usePrivilegesValidation();

  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isShowHidden: false,
    },
  });

  useEffect(
    () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_NEXT_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_SAME_PAGE_ALERT, EAlertTime.SHORT);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.SHORT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_TRANSACTION_ALERT);
      clearAlert(ActionTypes.CLEAR_FIREARM_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    if (!weaponLicenceStatus) {
      getWeaponLicenceStatus();
    }
  }, []);

  useEffect(() => {
    (async function getFirearmDataInit() {
      setIsFirearmLoading(true);
      if (id) {
        const response = await getRegisteredFirearm(id);
        if (response) {
          setFirearm(response);
        }
        setIsFirearmLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async function getFirearmsTransactionsDataInit() {
      setIsTransactionPaginationLoading(true);
      await getDiscardableFirearmTransactionData(id as string);

      await getFirearmTransactionsData(
        id as string,
        currentPageFT,
        perPageFT,
        transactionQuery,
        transactionSort,
      );
    })();
    setIsTransactionPaginationLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageFT, perPageFT, transactionQuery, transactionSort, selectedLanguage]);

  useEffect(() => {
    (async function getFirearmsLicencesDataInit() {
      setIsLicencesPaginationLoading(true);
      await getFirearmLicencesData(
        id as string,
        currentPageLicences,
        perPageLicences,
        licenceSort,
      );
    })();
    setIsLicencesPaginationLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageLicences, perPageLicences, licenceSort, selectedLanguage]);

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    if (
      user &&
      user.userId &&
      hasPermissions([EPermission.VIEW_LE, EPermission.VIEW_ALL_LE], true)
    ) {
      getAssignedSelfLegalsToUserData();
    }
  }, [user]);

  const isShowHidden: boolean = watch('isShowHidden');
  const licenceAuthorityValid: boolean =
    !firearm || isAnyLicenceAuthorityValid(firearm.licenceAuthorityIds);

  useEffect(() => {
    (async function getFirearmsCommentsDataInit() {
      if (id) {
        setIsCommentPaginationLoading(true);
        setIsFirearmCommentsLoading(true);
        await getFirearmCommentsData(id, {
          withHidden: isShowHidden,
          currentPage: currentPageFComments,
          perPage: perPageFComments,
        });

        setIsFirearmCommentsLoading(false);
        setIsCommentPaginationLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isShowHidden, currentPageFComments, perPageFComments]);

  useEffect(() => {
    (async function getFirearmsAttachmentsDataInit() {
      setIsAttachmentPaginationLoading(true);
      await getFirearmAttachmentsData(id as string, currentPageFA, perPageFA, attachmentSort);
      setIsAttachmentPaginationLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageFA, perPageFA, attachmentSort]);

  useEffect(() => {
    (async function () {
      await loadDictionaries([EDictionaryTypes.TransactionType]);
    })();
  }, [selectedLanguage]);

  const handleDeleteAttachment = (attachment: IRegisteredFirearmAttachmentProps) => {
    navigate(
      `${ERouteLinks.RegisteredFirearmDeleteAttachment.replace(':id', id as string).replace(
        ':attachmentId',
        attachment.id,
      )}?expand=attachments`,
      { state: { attachment } },
    );
  };

  const handleToggleExpandAll = () => {
    setFirearmDetailsAllExpanded((prev) => !prev);
  };

  const handleAddTransaction = () => {
    if (firearm?.groupName && firearm.stateUniqueId !== EFirearmStateId.EXPORTED) {
      clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
      clearAlert(ActionTypes.SET_TRANSACTION_ALERT);
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {} as IAlert);
    } else {
      navigate(`${ERouteLinks.MiddleTransaction.replace(':id', id as string)}`, {
        state: {
          isTransactionForGroup: '0',
          firearm,
        },
      });
    }
  };

  const handleExportFirearmTransactions = async () => {
    if (id && transactionQuery) {
      setIsExportloading(true);

      transactionQuery.transactionDateFrom = transactionQuery.transactionDateFrom
        ? formatDateTimeToIso(transactionQuery.transactionDateFrom)
        : undefined;
      transactionQuery.transactionDateTo = transactionQuery.transactionDateTo
        ? formatDateTimeToIso(transactionQuery.transactionDateTo)
        : undefined;

      await exportFirearmTransactionsCSV(id, transactionQuery, transactionSort);
      setIsExportloading(false);
    }
  };

  const isAddTransactionVisible = () => {
    if (
      [EFirearmStateId.DISABLED, EFirearmStateId.DESTROYED].includes(
        firearm?.stateUniqueId as EFirearmStateId,
      )
    )
      return false;
    if (!licenceAuthorityValid) return false;
    if (hasPermissions(EPermission.VIEW_ALL_LE)) return true;
    if (hasPermissions(EPermission.VIEW_LE)) {
      return (
        (firearm?.keeperVisibilityUniqueId !== ELegalVisibility.NON_PUBLIC ||
          account?.assignedLegalIds.includes(firearm.keeperId)) &&
        (firearm?.ownerVisibilityUniqueId !== ELegalVisibility.NON_PUBLIC ||
          account?.assignedLegalIds.includes(firearm.ownerId))
      );
    }
    return false;
  };

  const handleDelete = (transactionId: string) => {
    if (!lastTransaction?.anyElementInDisabledState) {
      navigate(ERouteLinks.CancelTransaction.replace(':id', transactionId));
    } else {
      setAlert(ActionTypes.SET_FIREARM_ALERT, {
        text: t('transactionDiscard.elementInDisabledState.message'),
        variant: EAlertVariants.error,
      });
    }
  };

  const groupTransactionForbiddenAlert = (
    <Grid sx={{ mb: 3 }}>
      <Alert
        variant={EAlertVariants.warning}
        text={t('addTransaction.groupAssigned.alert.text')}
      />
    </Grid>
  );

  return (
    <>
      {routerState?.breadcrumb ? (
        <Breadcrumbs
          items={[...routerState.breadcrumb, t('registeredFirearms.firearmsDetails.header')]}
        />
      ) : (
        <Breadcrumbs
          items={
            isFromLicence
              ? [
                  { label: t('module.legalEntity.name'), route: ERouteLinks.LegalEntities },
                  {
                    label:
                      type === ELegalEntityTypes.individual
                        ? t('legalEntities.individual.accordion.label')
                        : t('legalEntities.organization.accordion.label'),
                    route: `${ERouteLinks.LegalEntities}?expand=${
                      type === ELegalEntityTypes.individual
                        ? LEAccordionNames.INDIVIDUAL
                        : LEAccordionNames.ORGANISATION
                    }`,
                  },
                  {
                    label: t('legalEntityDetails.legalEntityDetails.text'),
                    route: `${ERouteLinks.ViewLegalEntity.replace(
                      ':id',
                      legalEntityId as string,
                    )}?type=${
                      type === ELegalEntityTypes.individual
                        ? LEAccordionNames.INDIVIDUAL
                        : LEAccordionNames.ORGANISATION
                    }&expand=${expand}`,
                  },
                  {
                    label: t('legalEntityDetails.view.licence.label'),
                    route: `${ERouteLinks.LegalEntityViewLicence.replace(
                      ':id',
                      legalEntityId,
                    ).replace(':licenceId', licenceId)}?type=${
                      type === ELegalEntityTypes.individual
                        ? LEAccordionNames.INDIVIDUAL
                        : LEAccordionNames.ORGANISATION
                    }&expand=${expand}`,
                  },
                  t('registeredFirearms.firearmsDetails.header'),
                ]
              : [
                  {
                    label: t('firearms.registeredFirearms.accordion.name'),
                    route: ERouteLinks.Firearms,
                  },
                  t('registeredFirearms.firearmsDetails.header'),
                ]
          }
        />
      )}
      <div>
        {isFirearmLoading ? (
          <Loader isLoading={isFirearmLoading} />
        ) : (
          firearm && (
            <RegisteredFirearmDetails
              firearm={firearm}
              disableManageActions={!licenceAuthorityValid}
              hideDisableButton={firearm.stateUniqueId === EFirearmStateId.DISABLED}
              changeLocationForItemInGroupCallback={() => {
                setAlert(ActionTypes.SET_TRANSACTION_ALERT, {} as IAlert);
                clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
              }}
            />
          )
        )}
      </div>
      {nextPageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}
      {firearmAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={firearmAlert.text} variant={firearmAlert.variant} />
        </Box>
      )}
      {transactionAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert
            variant={EAlertVariants.warning}
            text={t('addTransaction.groupAssigned.alert.text')}
          />
        </Box>
      )}
      {hasPermissions(
        [
          EPermission.VIEW_TRANSACTIONS,
          EPermission.MANAGE_COMMENTS_FIREARMS,
          EPermission.MANAGE_ATTACHMENTS_FIREARMS,
        ],
        true,
      ) && (
        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
        >
          <StyledButton
            label={
              firearmDetailsAllExpanded
                ? t('general.collapseAll.label')
                : t('general.expandAll.label')
            }
            onClick={handleToggleExpandAll}
            variant={EButtonVariants.outlined}
          />
        </Grid>
      )}
      <Grid container sx={{ mt: 2 }}>
        {hasPermissions(
          [EPermission.VIEW_GLOBAL_LICENCES, EPermission.VIEW_RESTRICTED_LICENCES],
          true,
        ) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              id="licences-accordion"
              title={t('registeredFirearms.firearmsDetails.licences.accordion.name')}
              expanded={firearmDetailsAllExpanded}
            >
              {isDesktop ? (
                <Table
                  columns={firearmLicencesTableColumns({
                    handleView: (licenceId: string, type: string) => {
                      navigate(
                        `${ERouteLinks.DetailLicence.replace(':licenceId', licenceId)}?type=${
                          type === LETypes.individual
                            ? ELegalEntityTypesLabel.individual
                            : ELegalEntityTypesLabel.organisation
                        }`,
                      );
                    },
                    dateFormat: shortDateFormat,
                    weaponLicenceStatus,
                  })}
                  dataSource={mapFirearmLicenceToDataSource(firearmLicences)}
                  onSort={setLicenceSort}
                />
              ) : (
                <CardList
                  items={mapFirearmLicenceToDataSource(firearmLicences)}
                  render={(data, index) => (
                    <FirearmLicenceTableCard
                      data={data}
                      dateFormat={shortDateFormat}
                      weaponLicenceStatus={weaponLicenceStatus}
                      key={index}
                    />
                  )}
                />
              )}
              {paginatorLicences && (
                <Pagination
                  count={paginatorLicences?.totalElements}
                  perPage={perPageLicences}
                  onChangePage={(page) => {
                    setCurrentPageLicences(page);
                  }}
                  onChangePerPage={(value) => {
                    setPerPageLicences(value);
                  }}
                  current={currentPageLicences}
                  isLoading={isLicencesPaginationLoading}
                />
              )}
            </Accordion>
          </Grid>
        )}

        {hasPermissions([EPermission.VIEW_FIREARMS, EPermission.VIEW_ALL_FIREARMS], true) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              id="transactions-accordion"
              title={t('registeredFirearms.firearmsDetails.transactions.accordion.name')}
              expanded={firearmDetailsAllExpanded}
            >
              <TransactionSearchForm
                showSaveFilterButton={false}
                onSubmit={setTransactionQuery}
              />
              <Grid
                container
                spacing={1}
                sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}
              >
                {!isFirearmLoading && (
                  <>
                    <Grid>
                      {isExportNotAvailable(paginatorFT) ? (
                        <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
                          <span>
                            <Button
                              id="transactions-export-button"
                              label={t('legalEntities.individual.accordion.export.button')}
                              variant={EButtonVariants.outlined}
                              permission={[EPermission.EXPORT_FIREARMS]}
                              onClick={handleExportFirearmTransactions}
                              isLoading={isExportLoading}
                              disabled
                              size={EButtonSizes.medium}
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          id="transactions-export-button"
                          label={t('legalEntities.individual.accordion.export.button')}
                          variant={EButtonVariants.outlined}
                          permission={[EPermission.EXPORT_FIREARMS]}
                          onClick={handleExportFirearmTransactions}
                          isLoading={isExportLoading}
                          size={EButtonSizes.medium}
                        />
                      )}
                    </Grid>
                    {isAddTransactionVisible() && (
                      <Grid>
                        <Button
                          id="add-transaction-button"
                          label={t(
                            'registeredFirearms.firearmsDetails.transactions.accordion.addTransaction,button',
                          )}
                          variant={EButtonVariants.contained}
                          icon={EIconTypes.plus}
                          onClick={handleAddTransaction}
                          permission={[EPermission.ADD_TRANSACTION]}
                          fullWidth={!isDesktop}
                          size={EButtonSizes.medium}
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
              {samePageAlert && groupTransactionForbiddenAlert}
              {isDesktop ? (
                <Table
                  columns={firearmTransactionsTableColumns({
                    discardableFirearmTransactionId,
                    showDetails: (props) => props.detailsVisible,
                    handleView: hasPermissions(
                      [EPermission.VIEW_ALL_TRANSACTIONS, EPermission.VIEW_TRANSACTIONS],
                      true,
                    )
                      ? (transactionId: string) =>
                          navigate(
                            `${ERouteLinks.ViewTransaction.replace(':id', transactionId)}`,
                          )
                      : null,
                    handleDelete:
                      licenceAuthorityValid &&
                      !lastTransaction?.discardDisabled &&
                      (hasPermissions([
                        EPermission.VIEW_ALL_TRANSACTIONS,
                        EPermission.DISCARD_TRANSACTION,
                      ]) ||
                        hasPermissions([
                          EPermission.VIEW_TRANSACTIONS,
                          EPermission.DISCARD_TRANSACTION,
                        ]))
                        ? (transactionId: string) => handleDelete(transactionId)
                        : null,
                    longDateFormat,
                  })}
                  dataSource={mapFirearmTransactionToDataSource(firearmTransactions)}
                  onSort={setTransactionSort}
                />
              ) : (
                <CardList
                  items={mapFirearmTransactionToDataSource(firearmTransactions)}
                  render={(data, index) => (
                    <FirearmTransactionTableCard
                      discardableFirearmTransactionId={discardableFirearmTransactionId}
                      discardDisabled={lastTransaction?.discardDisabled === true}
                      handleDelete={handleDelete}
                      data={data}
                      key={index}
                    />
                  )}
                />
              )}
              {paginatorFT && (
                <Pagination
                  count={paginatorFT?.totalElements}
                  perPage={perPageFT}
                  onChangePage={(page) => {
                    setCurrentPageFT(page);
                  }}
                  onChangePerPage={(value) => {
                    setPerPageFT(value);
                  }}
                  current={currentPageFT}
                  isLoading={isTransactionPaginationLoading}
                />
              )}
            </Accordion>
          </Grid>
        )}

        {hasPermissions([EPermission.MANAGE_COMMENTS_FIREARMS]) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('registeredFirearms.firearmsDetails.comments.accordion.name')}
              expanded={firearmDetailsAllExpanded}
            >
              <Grid
                container
                sx={{
                  marginBottom: { xs: '20px', sm: '16px' },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Grid container size={{ xs: 12, sm: 6 }}>
                  <FormCheckbox
                    name="isShowHidden"
                    label={t(
                      'registeredFirearms.firearmsDetails.comments.accordion.showHiddenComments.label',
                    )}
                    control={control}
                    errors={errors}
                    withValidation={false}
                  />
                </Grid>
                {licenceAuthorityValid && (
                  <Grid
                    size={{ xs: 12, sm: 6 }}
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <Button
                      id="firearms-details-add-comment"
                      label={t(
                        'registeredFirearms.firearmsDetails.comments.accordion.addComment.button',
                      )}
                      size={isMobile ? EButtonSizes.small : EButtonSizes.medium}
                      variant={EButtonVariants.contained}
                      icon={EIconTypes.plus}
                      onClick={() => {
                        navigate(
                          `${ERouteLinks.RegisteredFirearmAddComment.replace(
                            ':id',
                            id as string,
                          )}?expand=comments`,
                        );
                      }}
                      fullWidth={isMobile}
                    />
                  </Grid>
                )}
              </Grid>
              {isFirearmCommentsLoading ? (
                <Loader isLoading={isFirearmCommentsLoading} />
              ) : (
                firearmComments?.map((item, index) => (
                  <Box key={item.uniqueId} sx={{ marginBottom: '8px' }}>
                    <CommentAccordion
                      visibilityButtonId={`firearm-comment-visibility-button-${index}`}
                      dropdownIconId={`firearm-comment-dropdown-${index}`}
                      name="comments"
                      comment={item}
                      deleteLink={
                        licenceAuthorityValid
                          ? ERouteLinks.RegisteredFirearmDeleteComment
                          : undefined
                      }
                    />
                  </Box>
                ))
              )}
              {!firearmComments?.length && (
                <Typography
                  variant="subtitle2"
                  color={colors.primary100}
                  sx={{ mb: 2, textAlign: 'center' }}
                >
                  {t('registeredFirearms.accordion.noResults.text')}
                </Typography>
              )}
              {paginatorFComments && (
                <Pagination
                  count={paginatorFComments?.totalElements}
                  perPage={perPageFComments}
                  onChangePage={(page) => {
                    setCurrentPageFComments(page);
                  }}
                  onChangePerPage={(value) => {
                    setPerPageFComments(value);
                  }}
                  current={currentPageFComments}
                  isLoading={isCommentPaginationLoading}
                />
              )}
            </Accordion>
          </Grid>
        )}

        {hasPermissions([EPermission.MANAGE_ATTACHMENTS_FIREARMS]) && (
          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('registeredFirearms.firearmsDetails.attachments.accordion.name')}
              expanded={firearmDetailsAllExpanded}
              name="attachments"
            >
              <Grid
                sx={{
                  marginBottom: { xs: '20px', sm: '16px' },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {licenceAuthorityValid && (
                  <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      id="firearm-details-add-attachment"
                      label={t(
                        'registeredFirearms.firearmsDetails.attachments.accordion.addAttachments.button',
                      )}
                      variant={EButtonVariants.contained}
                      icon={EIconTypes.plus}
                      onClick={() => {
                        navigate(
                          `${ERouteLinks.RegisteredFirearmAddAttachment.replace(
                            ':id',
                            id as string,
                          )}?expand=attachments`,
                        );
                      }}
                      fullWidth={isMobile}
                      size={EButtonSizes.medium}
                    />
                  </Grid>
                )}
              </Grid>
              {isDesktop ? (
                <Table
                  id="firearm-attachment-table"
                  columns={firearmAttachmentsTableColumns({
                    firearmId: id as string,
                    handleDelete: handleDeleteAttachment,
                    showDelete: licenceAuthorityValid,
                    handleShowTransaction: () => {},
                    longDateFormat,
                  })}
                  dataSource={mapFirearmAttachmentToDataSource(
                    firearmAttachments,
                    transactionType,
                    midDateFormat,
                  )}
                  onSort={setAttachmentSort}
                />
              ) : (
                <CardList
                  items={mapFirearmAttachmentToDataSource(
                    firearmAttachments,
                    transactionType,
                    midDateFormat,
                  )}
                  render={(data, index) => (
                    <FirearmAttachmentTableCard
                      data={data}
                      showDelete={licenceAuthorityValid}
                      key={index}
                    />
                  )}
                />
              )}

              {paginatorFA && (
                <Pagination
                  count={paginatorFA?.totalElements}
                  perPage={perPageFA}
                  onChangePage={(page) => {
                    setCurrentPageFA(page);
                  }}
                  onChangePerPage={(value) => {
                    setPerPageFA(value);
                  }}
                  current={currentPageFA}
                  isLoading={isAttachmentPaginationLoading}
                />
              )}
            </Accordion>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export { ViewRegisteredFirearmPage };
